import React, { useState } from "react";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import { Button } from "@design-system/Button/Button";
import { CopyOnClick } from "@design-system/CopyOnClick/CopyOnClick";
import { DisabledWithTooltip } from "@design-system/DisabledWithTooltip/DisabledWithTooltip";
// oxlint-disable-next-line no-restricted-imports -- Legacy migration debt — do not copy into new code.
import { EllipsisWithTooltip } from "@design-system/EllipsisWithTooltip/EllipsisWithTooltip";
import { VStack } from "@design-system/VStack/VStack";
import {
    BooleanControl,
    RangeControl,
    TextareaControl,
    TextControl,
    VariantGrid,
    VariantItem,
} from "./playgroundControls";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";
import styles from "./TextFormattingPlayground.module.scss";
import gridStyles from "./playgroundControls.module.scss";

const DEFAULT_TEXT =
    "Quarterly bookkeeping review for Northwind Consulting and its international subsidiaries";

export const TextUtilitiesPlayground = () => {
    const [text, setText] = useState(DEFAULT_TEXT);
    const [containerWidth, setContainerWidth] = useState(280);
    const [disabledReason, setDisabledReason] = useState(
        "Complete the account connection before continuing.",
    );
    const [showBreakableTooltip, setShowBreakableTooltip] = useState(true);
    const [isDisabled, setIsDisabled] = useState(true);

    return (
        <SandboxLayout
            controls={
                <>
                    <section>
                        <h2>Content</h2>
                        <VStack gap={12}>
                            <TextareaControl
                                label="Text"
                                value={text}
                                rows={10}
                                onChange={setText}
                            />
                            <RangeControl
                                label={`Container width: ${containerWidth}px`}
                                value={containerWidth}
                                min={120}
                                max={640}
                                step={20}
                                onChange={setContainerWidth}
                            />
                            <TextControl
                                label="Disabled reason"
                                value={disabledReason}
                                onChange={setDisabledReason}
                            />
                        </VStack>
                    </section>
                    <section>
                        <h2>Features</h2>
                        <div className={styles.booleanGrid}>
                            <BooleanControl
                                label="Breakable text tooltip"
                                checked={showBreakableTooltip}
                                onChange={setShowBreakableTooltip}
                            />
                            <BooleanControl
                                label="Disable wrapped action"
                                checked={isDisabled}
                                onChange={setIsDisabled}
                            />
                        </div>
                    </section>
                </>
            }
        >
            <ShowcasePreview
                summary={`${containerWidth}px container`}
                width="full"
                output={JSON.stringify(
                    {
                        breakableTextProps: {
                            text,
                            showTooltip: showBreakableTooltip,
                        },
                        ellipsisProps: {
                            fullText: text,
                            overflowDirection: "horizontal",
                        },
                        copyOnClickProps: {
                            value: text,
                            tooltipText: "Copy this text",
                        },
                        disabledWithTooltipProps: {
                            reason: isDisabled ? disabledReason.trim() : null,
                        },
                        showcaseState: {
                            containerWidth,
                            isDisabled,
                        },
                    },
                    null,
                    2,
                )}
            >
                <div className={gridStyles.fillWidth}>
                    <VariantGrid title="Text utilities" columns="full">
                        <VariantItem label="BreakableText">
                            <div
                                className={styles.utilityStage}
                                style={{ width: containerWidth }}
                            >
                                <BreakableText
                                    text={text}
                                    showTooltip={showBreakableTooltip}
                                />
                            </div>
                        </VariantItem>
                        <VariantItem label="EllipsisWithTooltip">
                            <div
                                className={styles.utilityStage}
                                style={{ width: containerWidth }}
                            >
                                <EllipsisWithTooltip<HTMLSpanElement>
                                    fullText={text}
                                    overflowDirection="horizontal"
                                >
                                    {(ref) => (
                                        <span
                                            ref={ref}
                                            className={styles.ellipsis}
                                        >
                                            {text}
                                        </span>
                                    )}
                                </EllipsisWithTooltip>
                            </div>
                        </VariantItem>
                        <VariantItem label="CopyOnClick">
                            <div
                                className={styles.utilityStage}
                                style={{ width: containerWidth }}
                            >
                                <CopyOnClick
                                    value={text}
                                    className={styles.clickable}
                                    tooltipText="Copy this text"
                                >
                                    {text}
                                </CopyOnClick>
                            </div>
                        </VariantItem>
                        <VariantItem label="DisabledWithTooltip">
                            <div
                                className={styles.utilityStage}
                                style={{ width: containerWidth }}
                            >
                                <DisabledWithTooltip
                                    reason={
                                        isDisabled
                                            ? disabledReason.trim()
                                            : undefined
                                    }
                                >
                                    <Button
                                        type="button"
                                        variant="primary"
                                        size="md"
                                        disabled={isDisabled}
                                    >
                                        Continue
                                    </Button>
                                </DisabledWithTooltip>
                            </div>
                        </VariantItem>
                    </VariantGrid>
                </div>
            </ShowcasePreview>
        </SandboxLayout>
    );
};
